// src/pages/Public/EventPost/PublicEventPostDetail.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, CircularProgress, Alert, Chip,
  Paper, Divider, Button, Avatar
} from '@mui/material';
import {
  ArrowBack as BackIcon, CalendarToday as CalendarIcon,
  Person as PersonIcon, Visibility as ViewIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const PublicEventPostDetail = () => {
  const { chatbotId, slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${apiBaseUrl}/api/public/event-posts/${chatbotId}/slug/${slug}`
        );
        setPost(response.data.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Post not found');
        } else {
          setError('Failed to load post. Please try again later.');
        }
        console.error('Error loading post:', err);
      } finally {
        setLoading(false);
      }
    };

    if (chatbotId && slug) loadPost();
  }, [chatbotId, slug, apiBaseUrl]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/posts/${chatbotId}`)}>
          Back to Posts
        </Button>
      </Container>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Featured Image Header */}
      {post.featured_image_url && (
        <Box
          sx={{
            width: '100%',
            height: { xs: 250, md: 400 },
            backgroundImage: `url(${post.featured_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '50%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)'
            }
          }}
        />
      )}

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Back Button */}
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/posts/${chatbotId}`)}
          sx={{ mb: 3 }}
        >
          Back to Posts
        </Button>

        <Paper sx={{ p: { xs: 3, md: 5 } }}>
          {/* Categories */}
          {post.categories && post.categories.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {post.categories.map(cat => (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  sx={{
                    mr: 1,
                    mb: 1,
                    bgcolor: cat.color,
                    color: '#fff'
                  }}
                />
              ))}
            </Box>
          )}

          {/* Title */}
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            {post.title}
          </Typography>

          {/* Meta Info */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              mb: 4,
              color: 'text.secondary'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" />
              <Typography variant="body2">
                {post.author_name || 'Anonymous'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon fontSize="small" />
              <Typography variant="body2">
                {formatDate(post.published_at)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewIcon fontSize="small" />
              <Typography variant="body2">
                {post.view_count || 0} views
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Content */}
          <Box
            sx={{
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 2,
                my: 2
              },
              '& p': {
                lineHeight: 1.8,
                mb: 2
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 4,
                mb: 2,
                fontWeight: 'bold'
              },
              '& ul, & ol': {
                pl: 3,
                mb: 2
              },
              '& li': {
                mb: 1
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'underline'
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                my: 2,
                fontStyle: 'italic',
                color: 'text.secondary'
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                my: 2
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1.5,
                textAlign: 'left'
              },
              '& th': {
                bgcolor: 'grey.100',
                fontWeight: 'bold'
              },
              '& iframe': {
                maxWidth: '100%',
                my: 2,
                borderRadius: 1
              }
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Media (Videos) */}
          {post.media && post.media.length > 0 && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" gutterBottom>
                Related Media
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {post.media.filter(m => m.media_type !== 'IMAGE').map(media => (
                  <Box key={media.id}>
                    {(media.media_type === 'YOUTUBE' || media.media_type === 'VIMEO') && (
                      <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                        <iframe
                          src={media.embed_url}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0
                          }}
                          allowFullScreen
                          title={media.video_title || 'Video'}
                        />
                      </Box>
                    )}
                    {media.caption && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {media.caption}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Linked Event */}
          {post.linked_event_name && (
            <>
              <Divider sx={{ my: 4 }} />
              <Box sx={{ bgcolor: 'primary.light', p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="primary.contrastText">
                  Related Event
                </Typography>
                <Typography variant="h6" color="primary.contrastText">
                  {post.linked_event_name}
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicEventPostDetail;
